import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useLanguage } from "../../Language";
import { useDisplayMapItButton, useStackMap } from "../../Stackmap";
import { StackMapButton } from "./StackMapButton";
import { ResourceDWR } from "../../../dto/ResourceDWR";
import { HoldingsInformation } from "../../../dto/HoldingsInformation";
import { ItemAvailability, useItemAvailability } from "./ItemAvailability";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { useGlobalConfig } from "../../GlobalConfig";
import { visuallyHidden } from "@mui/utils";

export const CopyCollectionNameDisplay = ({
  holding,
}: {
  holding: HoldingsInformation;
}) => {
  return <span>{holding?.collectionName}</span>;
};

export const CopyPrimaryDisplay = ({
  holding,
}: {
  holding: HoldingsInformation;
}) => {
  const shelfLocationContent = holding.serialItem ? (
    <CopySerialInfoDisplay holding={holding} />
  ) : (
    <CopyShelfLocationDisplay holding={holding} />
  );
  const hasShelfLocation = holding.serialItem
    ? Boolean(holding.chronology || holding.enumeration)
    : true;

  return (
    <Stack direction={"row"} gap={1} sx={{ flexWrap: "wrap" }}>
      {hasShelfLocation && <Typography>{shelfLocationContent}</Typography>}
      {hasShelfLocation && (
        <Divider orientation="vertical" variant="middle" flexItem />
      )}
      <Typography noWrap={true}>
        <CopyCollectionNameDisplay holding={holding} />
      </Typography>
      <Box sx={visuallyHidden}>{holding.branchName}</Box>
    </Stack>
  );
};

export const CopyShelfLocationDisplay = ({
  holding,
}: {
  holding: HoldingsInformation;
}) => {
  const getVolumeAndCopyFromHolding = function (holding: HoldingsInformation) {
    if (!holding) {
      return "";
    }

    let holdingWithVolumeAndCopy =
      (holding.volume ? holding.volume + " " : "") +
      (holding.copy ? holding.copy + " " : "") +
      (holding.year ? holding.year : "");

    return holdingWithVolumeAndCopy.trim();
  };

  const getPartsInfo = (holding: HoldingsInformation) => {
    if (!holding || !holding.parts) return "";
    return holding.parts;
  };

  const display = `${
    holding.shelfLocation ? holding.shelfLocation + " " : ""
  }${getVolumeAndCopyFromHolding(holding)} ${getPartsInfo(holding)}`.trim();

  if (display.length === 0) {
    return null;
  }
  return <span>{display}</span>;
};

export const CopyStatusDisplay = ({
  availability,
}: {
  availability: ItemAvailability;
}) => {
  const { translate } = useLanguage();
  const { globalSettings } = useGlobalConfig();
  const { showDueDateForItems } = globalSettings || {};

  if (!availability) {
    return <Skeleton variant="text" width={"50%"} />;
  }

  const { status, available, dueDate, dueDateString } = availability;
  return (
    <>
      <span className={status && available ? "available" : ""}>{status} </span>
      <br />
      {showDueDateForItems && dueDate ? (
        <strong>
          {translate("dueBack")} {dueDateString}
        </strong>
      ) : (
        ""
      )}
    </>
  );
};

export const CopySerialInfoDisplay = ({
  holding,
}: {
  holding: HoldingsInformation;
}) => {
  if (!holding.chronology && !holding.enumeration) {
    return null;
  }
  return (
    <Stack direction={"row"} gap={1} sx={{ flexWrap: "wrap" }}>
      <Typography component={"span"} noWrap={true}>
        {holding.chronology}
      </Typography>
      <Typography component={"span"} noWrap={true}>
        {holding.enumeration}
      </Typography>
    </Stack>
  );
};

export const serialSort = (
  lhs: HoldingsInformation,
  rhs: HoldingsInformation,
) => {
  const leftSort = lhs.chronologySort + lhs.enumerationSort;
  const rightSort = rhs.chronologySort + rhs.enumerationSort;
  if (leftSort < rightSort) {
    return 1;
  } else if (leftSort > rightSort) {
    return -1;
  }
  return 0;
};

export interface HoldingsProps {
  holdings: HoldingsInformation[];
  resource: ResourceDWR;
}
export const Holdings: React.FC<HoldingsProps> = ({ holdings, resource }) => {
  const { translate } = useLanguage();
  const isMobile = useIsMobile();
  const { openMapItDialog, stackMapEnabled } = useStackMap();
  const { itemAvailability, ready } = useItemAvailability(holdings, resource);
  const displayMapItButton = useDisplayMapItButton(
    holdings,
    itemAvailability,
    ready,
    resource,
  );

  const sortedHoldings = resource.serial ? holdings.sort(serialSort) : holdings;

  const mapItButton = (holding: HoldingsInformation) => {
    return displayMapItButton.get(holding.id) ? (
      <StackMapButton onClick={() => openMapItDialog(holding.id)} />
    ) : (
      <span>&nbsp;</span>
    );
  };

  if (isMobile) {
    return (
      <List>
        {sortedHoldings.map((hi) => {
          return (
            <ListItem divider key={hi.barcode} disablePadding disableGutters>
              <ListItemText
                primary={<CopyPrimaryDisplay holding={hi} />}
                secondary={
                  <Stack direction={"row"} gap={1}>
                    <Typography padding={0}>
                      <CopyStatusDisplay
                        availability={ready && itemAvailability.get(hi.barcode)}
                      />
                    </Typography>
                  </Stack>
                }
              />
              <ListItemIcon>{mapItButton(hi)}</ListItemIcon>
            </ListItem>
          );
        })}
      </List>
    );
  }

  return (
    <Table component="table" sx={{ tableLayout: "fixed", width: "100%" }}>
      {/* 600px - 40px - 18px in padding */}
      <colgroup>
        {/* Collection */}
        <col style={{ minWidth: "113px" }} />
        {/* Shelf/Serial */}
        <col style={{ minWidth: "113px" }} />
        {/* Status */}
        <col style={{ minWidth: "113px" }} />
        {/* Map It */}
        <col style={{ minWidth: "203px", width: "203px" }} />
      </colgroup>
      <TableHead component="thead">
        <TableRow component="tr">
          <TableCell component="th">{translate("collection")}</TableCell>
          <TableCell component="th">
            {resource.serial
              ? translate("serialInfo")
              : translate("shelfLocation")}
          </TableCell>
          <TableCell component="th">{translate("status")}</TableCell>
          <TableCell component="th">
            {stackMapEnabled ? translate("stackMapColumnHeader") : ""}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody component="tbody">
        {sortedHoldings.map((holding) => {
          const availability = itemAvailability.get(holding.barcode);

          return (
            <TableRow component="tr" key={holding.id}>
              <TableCell component="td" scope="row">
                <CopyCollectionNameDisplay holding={holding} />
              </TableCell>
              <TableCell component="td">
                {resource.serial ? (
                  <CopySerialInfoDisplay holding={holding} />
                ) : (
                  <CopyShelfLocationDisplay holding={holding} />
                )}
              </TableCell>
              <TableCell component="td">
                <CopyStatusDisplay availability={availability} />
              </TableCell>
              <TableCell component="td">{mapItButton(holding)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
